import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Fetch_newarrivals } from "../../../hooks/APIHooks";
import { BsBag } from "react-icons/bs";
import { IoMdLogIn } from "react-icons/io";
import { FaStar } from "react-icons/fa";

const NewArrivals = () => {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [imageErrors, setImageErrors] = useState({});
  const navigate = useNavigate();

  const getImageUrl = (imagePath) => {
    if (!imagePath) {
      return '';
    }
    
    if (imagePath.startsWith('http')) return imagePath;
    
    const cleanPath = imagePath.replace(/^(\.?\/?)?/, '');
    
    // Handle different environments
    let baseUrl;
    const { hostname, port, protocol } = window.location;
    
    if (hostname === 'localhost' && port === '3000') {
      // React dev server on port 3000, APIs on port 80
      baseUrl = `${protocol}//${hostname}`;
    } else if (hostname === 'localhost' && !port) {
      // React build served from same domain as APIs
      baseUrl = window.location.origin;
    } else {
      // Production environment
      baseUrl = window.location.origin;
    }
    
    return `${baseUrl}/apis/${cleanPath}`;
  };

  const handleImageError = (productId) => {
    setImageErrors(prev => ({
      ...prev,
      [productId]: true
    }));
  };

  const handleImageLoad = (productId) => {
    setImageErrors(prev => ({
      ...prev,
      [productId]: false
    }));
  };

  const fetchAllProducts = async (pageNumber) => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${Fetch_newarrivals}?page=${pageNumber}`);
      const data = await response.json();
      if (data.status === "success") {
        // Append new products to the existing products
        setProducts((prevProducts) => [...prevProducts, ...data.data]);
      } else {
        throw new Error(data.message || "Failed to fetch products");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllProducts(page);
  }, [page]);

  useEffect(() => {
    const savedCart = JSON.parse(localStorage.getItem("cart")) || [];
    setCart(savedCart);
  }, []);

  const handleAddToCart = (product) => {
    const existingProduct = cart.find(
      (item) => item.product_id === product.product_id
    );

    let updatedCart;
    if (existingProduct) {
      updatedCart = cart.map((item) =>
        item.product_id === product.product_id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
    } else {
      updatedCart = [...cart, { ...product, quantity: 1 }];
    }

    setCart(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    navigate("/Store/cart");
  };

  const handleLoadMore = () => {
    setPage((prev) => prev + 1);
  };

  const getFallbackContent = (product) => {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-200">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-600 mb-2">
            {product.product_name ? product.product_name.charAt(0).toUpperCase() : '?'}
          </div>
          <div className="text-xs text-gray-500 px-2">
            {product.product_name || 'No Image'}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mx-auto my-8">
      <h1 className="text-xl font-bold mb-4 text-center sm:text-2xl">
        New Arrivals
      </h1>

      {loading ? (
        <div className="text-center text-lg py-8">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-500 py-8">{error}</div>
      ) : products.length === 0 ? (
        <div className="text-center text-gray-500 py-8">No products available.</div>
      ) : (
        <div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 p-4">
            {products.map((item) => (
              <div
                key={item.product_id}
                className="relative flex flex-col cursor-pointer border rounded-lg hover:shadow-lg transition-shadow duration-300 bg-white overflow-hidden"
                onClick={() =>
                  navigate(
                    `/Store/sub-catalogue/products/product-details/${item.product_id}`
                  )
                }
              >
                <div className="relative w-full h-42 sm:h-58 overflow-hidden bg-gray-100">
                  {item.product_image && !imageErrors[item.product_id] ? (
                    <img
                      src={getImageUrl(item.product_image)}
                      alt={item.product_name}
                      className="object-cover object-top w-full h-full"
                      onError={() => handleImageError(item.product_id)}
                      onLoad={() => handleImageLoad(item.product_id)}
                    />
                  ) : (
                    getFallbackContent(item)
                  )}
                  {item.product_type !== 'multivariate' && (
                    <button
                      className="absolute top-2 right-2 bg-black rounded-full text-white text-xs p-2 transition-transform hover:scale-105 z-10"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddToCart(item);
                      }}
                    >
                      <BsBag size={15} />
                    </button>
                  )}
                </div>
                <div className="mt-2 p-4 flex-1">
                  <span className="text-xs text-gray-500 uppercase tracking-wide">
                    {item.catalogue_name}
                  </span>
                  <p
                    className="font-medium text-sm sm:text-base line-clamp-2 overflow-hidden text-ellipsis leading-[1.5rem] min-h-[3rem] mt-1"
                    title={item.product_name || "Unknown Product"}
                  >
                    {item.product_name || "Unknown Product"}
                  </p>
                  {item.product_type !== "multivariate" && (
                    <p className="text-lg sm:text-xl font-bold text-gray-900 mt-2">
                      ₹{item.price || "N/A"}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center space-x-1 bg-green-700 text-white px-2 py-1 rounded-full">
                      <span className="text-sm">{item.product_rating}</span>
                      <FaStar size={12} />
                    </div>
                    {item.product_type === "multivariate" && (
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        Multiple Variants
                      </span>
                    )}
                  </div>
                </div>
                {item.product_type === 'multivariate' && item.variants && item.variants.length > 0 && (
                  <div className="mt-2 px-4 pb-4">
                    <h3 className="font-bold text-sm mb-2">Variants:</h3>
                    <div className="space-y-1">
                      {item.variants.slice(0, 3).map((variant) => (
                        <div key={variant.option_id} className="flex justify-between text-xs bg-gray-50 px-2 py-1 rounded">
                          <span className="truncate">{variant.option_value}</span>
                          <span className="font-semibold">₹{variant.variant_prices}</span>
                        </div>
                      ))}
                      {item.variants.length > 3 && (
                        <div className="text-xs text-gray-500 text-center">
                          +{item.variants.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-center mt-8">
            <button
              onClick={handleLoadMore}
              className="bg-primary text-black py-3 px-8 rounded-md shadow-md hover:bg-yellow-600 transition duration-300 flex items-center justify-center group"
            >
              <IoMdLogIn className="mr-2 text-xl transition-transform duration-300 group-hover:translate-x-1" />
              Load More
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewArrivals;